import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Building2, Shield } from 'lucide-react';
import type { FacilityType } from '@his/shared';
import { ApiError, get, session } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { storedUiLocale, useLanguage, useTranslation } from '@/lib/i18n';
import { Alert, Button, Card, CardContent, Field, Input, PasswordInput } from '@/components/ui/primitives';

/**
 * Sign-in for both audiences: hospital staff (tenant resolved from the
 * subdomain, or typed when running on localhost) and platform staff.
 *
 * The TOTP field appears only once the server asks for it, so the common case
 * stays two fields.
 */
export function LoginPage() {
  const { signIn, signInPlatform } = useAuth();
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  // LIMS-parity entry points (../lims mounts its owner/client portals on
  // dedicated URLs): typing /owner or /client cold lands here via AppShell's
  // redirect with the wanted path in `location.state.from`, and `?mode=` on
  // the URL works too — either way the right door is pre-selected and the
  // sign-in returns to where the user was headed instead of the dashboard.
  const from = (location.state as { from?: string } | null)?.from;
  const wantedMode: Mode | null =
    new URLSearchParams(location.search).get('mode') === 'platform' || from?.startsWith('/owner')
      ? 'platform'
      : new URLSearchParams(location.search).get('mode') === 'client' || from?.startsWith('/client')
        ? 'client'
        : null;

  // Three doors, two endpoints. A contact person signs in exactly as platform
  // staff do — same table, same token — so the third mode only changes the
  // wording and where they land.
  const [mode, setMode] = useState<Mode>(wantedMode ?? 'tenant');
  const [tenant, setTenant] = useState(() => session.tenant() ?? guessTenantFromHost());
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Once a subdomain is typed, the door says whose it is — a pharmacy signs
  // in to its pharmacy, not to "your hospital". Debounced, public, and silent
  // on failure: an unknown subdomain simply shows the generic title.
  const [branding, setBranding] = useState<{ name: string; facilityType: FacilityType; logoUrl: string | null; defaultUiLocale?: string } | null>(null);
  const { setPreviewLocale } = useLanguage();
  useEffect(() => {
    if (branding?.defaultUiLocale && !storedUiLocale()) setPreviewLocale(branding.defaultUiLocale);
  }, [branding, setPreviewLocale]);
  useEffect(() => {
    const slug = tenant.trim().toLowerCase();
    if (mode !== 'tenant' || slug.length < 3) {
      setBranding(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      get<{ name: string; facilityType: FacilityType; logoUrl: string | null; defaultUiLocale?: string }>(
        `/auth/tenant-branding?slug=${encodeURIComponent(slug)}`,
      )
        .then((found) => {
          if (!cancelled) setBranding(found);
        })
        .catch(() => {
          if (!cancelled) setBranding(null);
        });
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [tenant, mode]);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const user =
        mode === 'tenant'
          ? await signIn(tenant, identifier, password, totp || undefined)
          : await signInPlatform(identifier, password, totp || undefined);

      if (user.requires2fa && !user.has2fa) {
        navigate('/two-factor', { replace: true });
        return;
      }
      navigate(from ?? landingFor(user.isPlatformUser, user.platformRole), { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'two_factor_required') {
        setNeedsTotp(true);
        setError(t('signin.enter_code'));
      } else {
        setError(describeError(caught));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          {branding?.logoUrl ? (
            <img src={branding.logoUrl} alt="" className="mx-auto h-12 w-12 rounded-xl object-contain" />
          ) : (
            <div className="mx-auto grid h-12 w-12 place-items-center rounded-xl bg-primary text-primary-foreground">
              <span className="text-sm font-bold">HIS</span>
            </div>
          )}
          <h1 className="mt-3 text-xl font-semibold">{branding?.name ?? t('signin.app_name')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t(branding ? FACILITY_SIGN_IN[branding.facilityType] : SUBTITLE[mode])}
          </p>
        </div>

        <Card>
          <CardContent className="pt-4">
            <form onSubmit={submit} className="space-y-4">
              {mode === 'tenant' && (
                <Field
                  label={t('signin.organisation')}
                  htmlFor="tenant"
                  required
                  hint={t('signin.organisation_hint')}
                >
                  <div className="relative">
                    <Building2 className="pointer-events-none absolute start-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="tenant"
                      className="ps-8"
                      value={tenant}
                      onChange={(e) => setTenant(e.target.value)}
                      autoComplete="organization"
                      required
                    />
                  </div>
                </Field>
              )}

              <Field label={t(mode === 'tenant' ? 'signin.username' : 'signin.email')} htmlFor="identifier" required>
                <Input
                  id="identifier"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  autoComplete="username"
                  autoFocus
                  required
                />
              </Field>

              <Field label={t('signin.password')} htmlFor="password" required>
                <PasswordInput
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </Field>

              {needsTotp && (
                <Field label={t('signin.code')} htmlFor="totp" required>
                  <div className="relative">
                    <Shield className="pointer-events-none absolute start-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="totp"
                      className="ps-8 tracking-[0.3em]"
                      inputMode="numeric"
                      maxLength={6}
                      value={totp}
                      onChange={(e) => setTotp(e.target.value.replace(/\D/g, ''))}
                      autoFocus
                      required
                    />
                  </div>
                </Field>
              )}

              {error && <Alert variant="destructive">{error}</Alert>}

              <Button type="submit" className="w-full" loading={busy}>
                {t('signin.submit')}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {(['tenant', 'client', 'platform'] as Mode[])
            .filter((option) => option !== mode)
            .map((option) => (
              <button
                key={option}
                type="button"
                className="underline-offset-4 hover:underline"
                onClick={() => {
                  setMode(option);
                  setError(null);
                  setNeedsTotp(false);
                }}
              >
                {t(SWITCH_LABEL[option])}
              </button>
            ))}
        </div>

        {/* Patients land here by mistake more often than anyone expects; this
            is the one link that is not for staff. */}
        <p className="mt-3 text-center text-xs text-muted-foreground">
          {t('signin.directory_prompt')}{' '}
          <a className="font-medium text-primary underline-offset-4 hover:underline" href="/find">
            {t('signin.directory_link')}
          </a>
        </p>
      </div>
    </div>
  );
}

type Mode = 'tenant' | 'client' | 'platform';

const SUBTITLE: Record<Mode, string> = {
  tenant: 'signin.subtitle.tenant',
  client: 'signin.subtitle.client',
  platform: 'signin.subtitle.platform',
};

const SWITCH_LABEL: Record<Mode, string> = {
  tenant: 'signin.switch.tenant',
  client: 'signin.switch.client',
  platform: 'signin.switch.platform',
};

const FACILITY_SIGN_IN: Record<FacilityType, string> = {
  hospital: 'signin.facility.hospital',
  lab: 'signin.facility.lab',
  pharmacy: 'signin.facility.pharmacy',
};

/**
 * Where a successful sign-in lands.
 *
 * A contact person shares the platform token with owner staff but not their
 * console: sending them to `/owner` would show a shell whose every request
 * comes back 403.
 */
function landingFor(isPlatformUser: boolean, platformRole?: string): string {
  if (!isPlatformUser) return '/dashboard';
  return platformRole === 'contact_person' ? '/client' : '/owner';
}

/** On `shifa.platform.com` the tenant is already known; on localhost it is not. */
function guessTenantFromHost(): string {
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return '';
  // A bare-IP deployment (no domain yet) has no subdomain to read — without
  // this, "95.111.x.y" would offer its first octet as the tenant slug.
  if (/^\d+\.\d+\.\d+\.\d+$/.test(host) || host.includes(':')) return '';
  const [label] = host.split('.');
  return ['www', 'app', 'admin', 'api', 'owner', 'platform'].includes(label) ? '' : label;
}
